import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { cx } from 'class-variance-authority'
import { CheckIcon } from '@phosphor-icons/react/dist/ssr/Check'
import { CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { StackIcon } from '@phosphor-icons/react/dist/ssr/Stack'

type DropdownMenuProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Root
> & {
  inset?: boolean
  className?: string
  disabled?: boolean
}

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuProps
>((props, ref) => {
  const { className, inset, children, ...rest } = props
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cx(
        'focus:bg-accent data-[state=open]:bg-accent m-1 flex cursor-default select-none items-center rounded-sm px-2 py-2 text-sm text-slate-500 outline-none transition-colors hover:bg-primary-500 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className,
      )}
      {...rest}
    >
      {children}
      <CaretRightIcon className='ml-auto size-4' />
    </DropdownMenuPrimitive.SubTrigger>
  )
})
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

type DropdownMenuSubContentProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubContent
> & { className?: string }

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>((props, ref) => {
  const { className, ...rest } = props
  return (
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cx(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[10rem] overflow-hidden rounded-md border bg-zinc-50 shadow-lg',
        className,
      )}
      {...rest}
    />
  )
})
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

type DropdownMenuContentProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
> & {
  sideOffset?: number
  className?: string
}

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>((props, ref) => {
  const { className, sideOffset = 4, ...rest } = props
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cx(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] overflow-hidden rounded-md border bg-zinc-50 shadow-md',
          className,
        )}
        {...rest}
      />
    </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

type DropdownMenuItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> & {
  inset?: boolean
  className?: string
}

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>((props, ref) => {
  const { className, inset, ...rest } = props
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cx(
        'focus:bg-accent focus:text-accent-foreground relative m-1 flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm text-slate-500 outline-none transition-colors hover:bg-primary-500 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className,
      )}
      {...rest}
    />
  )
})
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

type DropdownMenuCheckboxItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
> & {
  className?: string
  checked?: boolean
}

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  DropdownMenuCheckboxItemProps
>((props, ref) => {
  const { className, children, checked, ...rest } = props
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cx(
        'focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-primary-500  data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      checked={checked}
      {...rest}
    >
      <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className='size-4' />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
})
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

type DropdowmMenuRadioItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.RadioItem
> & {
  className?: string
}

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdowmMenuRadioItemProps
>((props, ref) => {
  const { className, children, ...rest } = props
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cx(
        'focus:bg-accent focus:text-accent-foreground relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...rest}
    >
      <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
        <DropdownMenuPrimitive.ItemIndicator>
          <StackIcon className='size-2 fill-current' />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
})
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

type DropdownMenuLabelProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Label
> & {
  inset?: boolean
  className?: string
}

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  DropdownMenuLabelProps
>((props, ref) => {
  const { className, inset, ...rest } = props
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cx(
        'truncate whitespace-nowrap px-2 py-1.5 text-sm font-medium text-slate-300',
        inset && 'pl-8',
        className,
      )}
      {...rest}
    />
  )
})
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

type DropdownMenuSeparatorProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Separator
> & { className?: string }

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  DropdownMenuSeparatorProps
>((props, ref) => {
  const { className, ...rest } = props
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cx('-mx-1 h-px bg-border', className)}
      {...rest}
    />
  )
})
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

type DropdownMenuShortcutProps = React.HTMLAttributes<HTMLSpanElement> & {
  className?: string
}

const DropdownMenuShortcut = (props: DropdownMenuShortcutProps) => {
  const { className, ...rest } = props
  return (
    <span
      className={cx('ml-auto text-xs tracking-widest opacity-60', className)}
      {...rest}
    />
  )
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

type DropdownIconProps = React.ComponentPropsWithoutRef<'svg'> & {
  icon: React.ReactNode | string
}

const DropdownIcon = (props: DropdownIconProps) => {
  const { icon } = props
  if (typeof icon === 'string' && icon.includes('<img')) {
    const iconName = icon.replace(/<img src="\/icon\/(.*)\.svg" \/>/, '$1')
    return (
      <img
        src={`/icon/${iconName}.svg`}
        alt={iconName}
        className='mr-2 size-4'
      />
    )
  } else if (typeof icon === 'string' && !icon.includes('<img')) {
    return <img src={`/icon/${icon}.svg`} alt={icon} className='mr-2 size-4' />
  } else {
    return <>{icon}</>
  }
}

export const DropdownMenu: {
  Root: typeof DropdownMenuPrimitive.Root
  Trigger: typeof DropdownMenuPrimitive.Trigger
  Content: typeof DropdownMenuContent
  Item: typeof DropdownMenuItem
  CheckboxItem: typeof DropdownMenuCheckboxItem
  RadioItem: typeof DropdownMenuRadioItem
  Label: typeof DropdownMenuLabel
  Separator: typeof DropdownMenuSeparator
  Shortcut: typeof DropdownMenuShortcut
  Group: typeof DropdownMenuPrimitive.Group
  Portal: typeof DropdownMenuPrimitive.Portal
  Sub: typeof DropdownMenuPrimitive.Sub
  SubContent: typeof DropdownMenuSubContent
  SubTrigger: typeof DropdownMenuSubTrigger
  RadioGroup: typeof DropdownMenuPrimitive.RadioGroup
  Icon: typeof DropdownIcon
} = {
  Root: DropdownMenuPrimitive.Root,
  Trigger: DropdownMenuPrimitive.Trigger,
  Content: DropdownMenuContent,
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioItem: DropdownMenuRadioItem,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  Group: DropdownMenuPrimitive.Group,
  Portal: DropdownMenuPrimitive.Portal,
  Sub: DropdownMenuPrimitive.Sub,
  SubContent: DropdownMenuSubContent,
  SubTrigger: DropdownMenuSubTrigger,
  RadioGroup: DropdownMenuPrimitive.RadioGroup,
  Icon: DropdownIcon,
}
